/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  signal,
  ViewChild,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { assets } from "../../core/constants/assets.const";
import { BadgeModule } from "primeng/badge";
import { Router, RouterModule } from "@angular/router";
import { CommonModule } from "@angular/common";
import { Menu } from "primeng/menu";
import { ButtonModule } from "primeng/button";
import { NgxUiLoaderService } from "ngx-ui-loader";
import { UserService } from "../../services/user.service";
import { DrawerModule } from "primeng/drawer";
import { DynamicDialogRef, DialogService } from "primeng/dynamicdialog";
import { NotificationService } from "../../services/notification.service";
import { MessagingService } from "../../services/messaging.service";
import { AppStateService } from "../../services/app-state.service";
import { WebsocketService } from "../../services/websocket.service";
import { GlobalSearchComponent } from "../components/global-search/global-search.component";
import { environment } from "../../../environments/environment";
import { getUserRoleFromToken } from "../../core/guards/auth-role.helper";

interface MenuItem {
  label: string;
  icon: string;
  routerLink: string;
}

@Component({
  selector: "quote-hub-header",
  standalone: true,
  imports: [
    RouterModule,
    BadgeModule,
    CommonModule,
    Menu,
    ButtonModule,
    DrawerModule,
    GlobalSearchComponent,
  ],
  providers: [DialogService, NotificationService],
  templateUrl: "./header.component.html",
  styleUrl: "./header.component.scss",
})
export class HeaderComponent {
  private ws = inject(WebsocketService);
  @ViewChild("userMenu") userMenu!: Menu;
  assetsConst = assets;
  visible = false;
  role = localStorage.getItem("role") ?? "";
  ref: DynamicDialogRef | undefined;
  private dialogservice = inject(DialogService);
  public router = inject(Router);
  private userService = inject(UserService);
  private readonly ngxLoader = inject(NgxUiLoaderService);
  private notificationService = inject(NotificationService);
  private appState = inject(AppStateService);
  private messagingService = inject(MessagingService);
  private destroyRef = inject(DestroyRef);

  protected recievedNotifications = signal<any>([]);
  protected unreadCount = signal<number>(0);
  protected limit = signal<number>(3);
  protected totalCount = signal<number>(0);
  protected showNotificationButton = signal<boolean>(false);

  userRole: string = "";

  // ============== REACTIVE USER INFO (from AppStateService) ==============
  protected user = this.appState.user;

  /** User's profile image URL — falls back to null (icon shown instead) */
  protected profileImage = computed(() => {
    const u = this.user();
    return u?.profile_image?.s3_file_url || u?.profile_image || null;
  });

  /** Full display name — falls back to localStorage values on first load */
  protected displayName = computed(() => {
    const u = this.user();
    if (!u)
      return `${localStorage.getItem("first_name") ?? ""} ${localStorage.getItem("last_name") ?? ""}`.trim();
    return `${u.first_name ?? ""} ${u.last_name ?? ""}`.trim();
  });

  /** Email address — falls back to localStorage */
  protected email = computed(() => {
    const u = this.user();
    return u?.email ?? localStorage.getItem("email") ?? "";
  });
  // =======================================================================

  constructor() {
    this.getNotifications(this.limit());
    effect(() => {
      const dot = this.notificationService.showDot();
      this.notificationService.refreshTick(); // track it
      this.getNotifications(this.limit());
    });

    // ✅ WebSocket: update unread notification count on new quote events
    this.ws
      .getMessages()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((msg) => {
        if (msg.type === "notification_added") {
          this.unreadCount.set(msg.unread_count);
        }
      });


  }
  showGlobalSearch(): boolean {
    const user = ["ADMIN", "SALES", "APPROVER", "BA","MARKETING"];
    const token = localStorage.getItem(environment.storageKey) ?? "";
    const role = getUserRoleFromToken(token).toUpperCase();
    this.userRole = role
    return user.includes(role);
  }
  enableNotifications(): void {
    this.messagingService.requestPermission();
    this.showNotificationButton.set(false);
  }

  items: MenuItem[] = [];

  navigate(): void {
    this.router.navigate(["/dashboard"]);
  }

  navigateDetail(id: string | number, notification: any): void {
    notification.is_read = true;
    this.readNotification(notification.id);
    if (id) {
      this.visible = false;
      this.router.navigateByUrl(`/quotes/${id}`);
    }
  }

  getNotifications(limit: number | null, type?: number): void {
    let query = `limit=${limit ? limit : this.limit()}`;
    if (type) {
      query += `&type=${type}`;
    }
    this.userService.getRecievedNotifications(query).subscribe({
      next: (res: any) => {
        this.recievedNotifications.set(res.results);
        this.unreadCount.set(res.unread_count);
        this.totalCount.set(res.count);
      },
    });
  }

  readNotification(id: number | string): void {
    this.userService.readNotification(id, { is_read: true }).subscribe({
      next: () => {
        this.getNotifications(this.limit());
      },
    });
  }

  updateNotifications(): void {
    this.limit.update((item) => item + 10);
    this.getNotifications(this.limit());
  }

  logout(): void {
    this.ngxLoader.start();
    const keysToClear = [
      "fcmToken",
      "QUOTE_HUB",
      "role",
      "first_name",
      "last_name",
      "email",
    ];
    keysToClear.forEach((key) => localStorage.removeItem(key));
    // ✅ Clear reactive user state so header resets
    this.appState.clearUser();
    this.ngxLoader.stop();
    this.router.navigate(["/auth/login"]);
  }

  routeToSettings(): void {
    this.userMenu.hide();
    this.router.navigate(["/settings"]);
  }
}
